import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { requestPasswordReset } from '../../services/api';

const FormContainer = styled.div`
  max-width: 420px;
  margin: 2rem auto;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  background-color: ${props => props.theme.cardBg};
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.primaryText};
`;

const FormSubtitle = styled.p`
  text-align: center;
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.primaryText};
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 0.25rem;
  font-size: 1rem;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #4299e1;
  color: #fff;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  &:hover { background-color: #3182ce; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const Message = styled.div`
  margin-top: 0.9rem;
  font-size: 0.9rem;
  color: ${props => props.$error ? (props.theme.error || '#e53e3e') : props.theme.secondaryText};
  text-align: center;
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 1.25rem;
  font-size: 0.9rem;
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    font-weight: 500;
  }
  a:hover {
    text-decoration: underline;
  }
`;

function ForgotPasswordForm() {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      setMessage('');
      return;
    }
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const res = await requestPasswordReset(email.trim());
      setMessage(res?.message || 'If an account exists for this email, a reset link has been sent.');
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to send reset link right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer theme={theme}>
      <FormTitle theme={theme}>Forgot Password</FormTitle>
      <FormSubtitle theme={theme}>
        Enter your email and we will send you a password reset link.
      </FormSubtitle>
      <Form onSubmit={onSubmit}>
        <Label theme={theme} htmlFor="forgot-email">Email</Label>
        <Input
          id="forgot-email"
          theme={theme}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Sending...' : 'Send reset link'}
        </Button>
      </Form>
      {message && <Message theme={theme}>{message}</Message>}
      {error && <Message theme={theme} $error>{error}</Message>}
      <BackLink theme={theme}>
        <Link to="/login">Back to login</Link>
      </BackLink>
    </FormContainer>
  );
}

export default ForgotPasswordForm;
